import { useMemo, useCallback } from 'react';
import { Button, Descriptions, Typography, Card } from 'antd';
import { useHistory } from 'react-router-dom';
import { formatDate, mapReferralStatus, ml } from '../services/utils';
import MyReferralsTieredBonus from './referral-card/MyReferralsTieredBonusComponent.jsx';

const { Title, Text } = Typography;

const MyReferralsBonusDetailsModal = ({
	allMultiLingualData,
	currentUser,
	data,
	status,
	currentCurrencyRate,
	currentCurrencySymbol,
	translatedTitle,
	bonusesData,
	currentTieredBonus,
	handleCancel,
}) => {
	const history = useHistory();

	// Computed values
	const languageCode = useMemo(
		() => currentUser?.languageCode || 'US',
		[currentUser]
	);

	const dateFormat = useMemo(() => currentUser?.dateFormat, [currentUser]);

	const hireDate = useMemo(
		() => formatDate(data?.hireDate, languageCode, dateFormat),
		[data?.hireDate, languageCode, dateFormat]
	);

	const processedBonuses = useMemo(() => {
		const bonuses =
			bonusesData ||
			[...(data?.bonuses || [])].sort((a, b) => {
				return a.earnedDate < b.earnedDate
					? -1
					: a.earnedDate > b.earnedDate
						? 1
						: 0;
			});
		return bonuses;
	}, [bonusesData, data?.bonuses]);

	const startDate = useMemo(() => {
		const isJobsPage = window.location.href.includes('/jobs/');
		let calculatedStartDate = '';

		if (processedBonuses.length > 0) {
			if (isJobsPage) {
				const firstBonus = processedBonuses[0];
				calculatedStartDate = formatDate(
					firstBonus?.startDate,
					languageCode,
					dateFormat
				);
			} else {
				const employeeBonus = processedBonuses.find(
					(bonus) => bonus?.recipientType === 'employee'
				);
				if (employeeBonus?.startDate) {
					calculatedStartDate = formatDate(
						employeeBonus.startDate,
						languageCode,
						dateFormat
					);
				}
			}
		}

		return calculatedStartDate;
	}, [processedBonuses, languageCode, dateFormat]);

	const bonusNotes = useMemo(() => {
		return processedBonuses
			.map((bonus) => bonus?.notes)
			.filter((note) => note && note !== '');
	}, [processedBonuses]);

	// Helper functions
	const getName = useCallback(() => {
		const firstName = data?.contact?.firstName;
		const lastName = data?.contact?.lastName;
		const URL = `/referrals/${data?.id}`;
		return { firstName, lastName, URL };
	}, [data]);

	const navigateTo = useCallback(
		(URL) => {
			history.push(URL);
		},
		[history]
	);

	const referredCandidate = useMemo(() => {
		return getName(data?.recipientType);
	}, [getName, data?.recipientType]);

	return (
		<div style={{ padding: '24px 0' }}>
			<Descriptions
				bordered
				column={1}
				size="small"
				labelStyle={{
					backgroundColor: '#fafafa',
					fontWeight: 'bold',
					color: '#000000',
					width: '180px',
					padding: '8px 12px',
				}}
				contentStyle={{
					padding: '8px 12px',
				}}
			>
				<Descriptions.Item label={ml('Job', currentUser, allMultiLingualData)}>
					<Button
						type="link"
						style={{
							padding: 0,
							color: '#1890ff',
							textDecoration: 'none',
							whiteSpace: 'normal',
							wordBreak: 'break-word',
							textAlign: 'left',
							height: 'auto'
						}}
						onClick={() => navigateTo(`/jobs/${data?.job?.id}`)}
					>
						{translatedTitle}
					</Button>
				</Descriptions.Item>

				<Descriptions.Item
					label={ml(
						`${mapReferralStatus('referred', currentUser.company)} Candidate`,
						currentUser,
						allMultiLingualData
					)}
				>
					<Button
						type="link"
						style={{ padding: 0, color: '#1890ff', textDecoration: 'none' }}
						onClick={() => navigateTo(referredCandidate.URL)}
					>
						{referredCandidate.firstName} {referredCandidate.lastName}
					</Button>
				</Descriptions.Item>

				<Descriptions.Item
					label={ml(
						`${mapReferralStatus('hired', currentUser.company)} Date`,
						currentUser,
						allMultiLingualData
					)}
				>
					{hireDate}
				</Descriptions.Item>

				<Descriptions.Item
					label={ml('Start Date', currentUser, allMultiLingualData)}
				>
					{startDate || <Text type="secondary">Not available</Text>}
				</Descriptions.Item>
			</Descriptions>

			{/* Bonus Payments Section */}
			{data?.referralType !== 'self' && (
				<Card size="small" style={{ marginTop: 16 }}>
					<Title level={5}>
						{ml('Bonus Payments', currentUser, allMultiLingualData)}
					</Title>

					<MyReferralsTieredBonus
						currentTieredBonus={currentTieredBonus}
						bonusesData={processedBonuses}
						referral={data}
						status={status}
						userGroup={data?.user?.userGroupId}
						currentCurrencyRate={currentCurrencyRate}
						currentCurrencySymbol={currentCurrencySymbol}
						currentUser={currentUser}
						allMultiLingualData={allMultiLingualData}
					/>
				</Card>
			)}

			{/* Bonus Notes Section */}
			<Card size="small" style={{ marginTop: 16 }}>
				<Title level={5}>
					{ml('Bonus Notes', currentUser, allMultiLingualData)}
				</Title>
				{bonusNotes.length > 0 ? (
					<div style={{ maxHeight: '200px', overflow: 'auto' }}>
						{bonusNotes.map((note, index) => (
							<div
								key={index}
								style={{
									marginBottom: 8,
									padding: 8,
									backgroundColor: '#fafafa',
									borderRadius: 4,
									border: '1px solid #f0f0f0',
								}}
							>
								<Text>{note}</Text>
							</div>
						))}
					</div>
				) : (
					<Text type="secondary">
						{ml('No notes added', currentUser, allMultiLingualData)}
					</Text>
				)}
			</Card>

			{/* Footer */}
			<div
				style={{
					textAlign: 'right',
					marginTop: 24,
				}}
			>
				<Button
					type="primary"
					size="large"
					onClick={handleCancel}
					style={{ minWidth: '120px' }}
				>
					{ml('Close', currentUser, allMultiLingualData)}
				</Button>
			</div>
		</div>
	);
};

export default MyReferralsBonusDetailsModal;
